import { downloadZip } from 'client-zip';

// GMT-797 (Porcja 2): eksport mediów gry po stronie klienta. Przeglądarka
// pobiera pliki plik-po-pliku (z retry) i pakuje je STREAMEM PROSTO NA DYSK
// (client-zip + File System Access API) — nigdy nie trzyma całości w RAM, więc
// nie ma OOM ani na serwerze, ani w przeglądarce, niezależnie od rozmiaru gry.

export interface ZipProgress {
  done: number;
  total: number;
  skipped: number;
}

// File System Access API (showSaveFilePicker) = Chromium (Chrome/Edge). Tylko
// tam robimy streaming na dysk; inne przeglądarki idą ścieżką backendową.
export const isClientZipSupported = (): boolean => typeof window !== 'undefined' && 'showSaveFilePicker' in window;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Pobranie pojedynczego pliku z 3 próbami (backoff). null = plik pominięty.
const fetchWithRetry = async (url: string, attempts = 3): Promise<Response | null> => {
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const response = await fetch(url);
      if (response.ok) {
        return response;
      }
    } catch {
      // ponawiamy
    }
    await wait(300 * (attempt + 1));
  }

  return null;
};

// Leniwy iterator dla client-zip: pobiera kolejny plik dopiero gdy zip go
// zażąda (jeden w locie) i raportuje postęp. Wadliwe pliki pomija.
async function* buildEntries(
  urls: string[],
  mediaLabel: string,
  extension: string,
  onProgress: (progress: ZipProgress) => void,
) {
  const total = urls.length;
  let done = 0;
  let skipped = 0;

  for (let index = 0; index < urls.length; index++) {
    const url = urls[index];
    const response = url ? await fetchWithRetry(url) : null;
    done += 1;

    if (!response) {
      skipped += 1;
      onProgress({ done, total, skipped });
      continue;
    }

    onProgress({ done, total, skipped });
    yield {
      name: `${mediaLabel}_${index + 1}.${extension}`,
      input: response,
    };
  }
}

type SaveFilePicker = (options: {
  suggestedName?: string;
  types?: { description?: string; accept: Record<string, string[]> }[];
}) => Promise<{ createWritable: () => Promise<WritableStream<Uint8Array>> }>;

// Streamuje ZIP mediów na dysk. Zwraca false gdy użytkownik anulował dialog
// zapisu, true po zapisaniu.
export const streamGameMediaZipToDisk = async ({
  urls,
  suggestedName,
  mediaLabel,
  extension,
  onProgress,
}: {
  urls: string[];
  suggestedName: string;
  mediaLabel: string;
  extension: string;
  onProgress: (progress: ZipProgress) => void;
}): Promise<boolean> => {
  const showSaveFilePicker = (window as unknown as { showSaveFilePicker: SaveFilePicker }).showSaveFilePicker;

  let handle: { createWritable: () => Promise<WritableStream<Uint8Array>> };
  try {
    handle = await showSaveFilePicker({
      suggestedName,
      types: [{ description: 'ZIP', accept: { 'application/zip': ['.zip'] } }],
    });
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      return false;
    }
    throw error;
  }

  const writable = await handle.createWritable();
  const zipResponse = downloadZip(buildEntries(urls, mediaLabel, extension, onProgress));

  await zipResponse.body!.pipeTo(writable);

  return true;
};
